import { useState, useCallback, useMemo } from 'react'
import { startOfMonth, addMonths, startOfDay, endOfDay } from 'date-fns'
import { useWeeklyCalendarData } from './useWeeklyCalendarData'
import {
  createMonthGrid,
  groupAvailabilityByDate,
  groupOpponentAvailabilityByDate,
  groupProposalsByDate,
  groupMatchesByDate,
} from '../utils/calendarHelpers'
import { expandRecurrence } from '../utils/recurrence'

/**
 * Custom hook for managing month calendar view state and data.
 * Reuses data from weekly calendar view for efficient data management,
 * or accepts external data for flexibility.
 *
 * When no external data is provided, automatically fetches data using useWeeklyCalendarData.
 * When external data is provided, skips the fetch to avoid unnecessary API calls and side effects.
 *
 * @param {Object} [externalData] - Optional external data source (if not provided, uses useWeeklyCalendarData)
 * @param {Array} [externalData.userAvailability] - User's availability slots
 * @param {Array} [externalData.opponentAvailability] - Opponent's availability slots
 * @param {Array} [externalData.unplayedOpponents] - List of unplayed opponents
 * @param {Array} [externalData.matchProposals] - Match proposals
 * @param {Array} [externalData.allMatches] - All scheduled matches
 * @param {boolean} [externalData.loading] - Loading state
 * @param {Function} [externalData.handleAvailabilitySave] - Save availability callback
 * @param {Function} [externalData.handleAvailabilityDelete] - Delete availability callback
 * @param {Function} [externalData.handleMatchProposalConfirm] - Confirm match proposal callback
 * @param {Function} [externalData.handleProposalAction] - Handle proposal action callback
 *
 * @returns {Object} Month calendar data and controls
 * @returns {Date} returns.currentMonth - Currently displayed month (start of month)
 * @returns {Array<Object>} returns.monthDays - 42-day grid for calendar display, each with:
 *   - date: Date object
 *   - dateString: ISO date string (YYYY-MM-DD)
 *   - isCurrentMonth: boolean
 *   - isLeading: boolean (from previous month)
 *   - isTrailing: boolean (from next month)
 * @returns {Object} returns.availabilityByDate - Availability grouped by date string (YYYY-MM-DD)
 * @returns {Object} returns.opponentAvailabilityByDate - Opponent availability grouped by date string (YYYY-MM-DD)
 * @returns {Array} returns.unplayedOpponents - List of unplayed opponents for name lookup
 * @returns {Object} returns.proposalsByDate - Match proposals grouped by date string (YYYY-MM-DD)
 * @returns {Object} returns.matchesByDate - Scheduled matches grouped by date string (YYYY-MM-DD)
 * @returns {boolean} returns.loading - Loading state from data fetch
 * @returns {Function} returns.navigateMonth - Navigate to next/prev month: (direction: 'next'|'prev') => void
 * @returns {Function} returns.goToToday - Jump to current month: () => void
 * @returns {Function} returns.handleAvailabilitySave - Save availability callback from data source
 * @returns {Function} returns.handleAvailabilityDelete - Delete availability callback from data source
 * @returns {Function} returns.handleMatchProposalConfirm - Confirm match proposal callback from data source
 * @returns {Function} returns.handleProposalAction - Handle proposal action callback from data source
 *
 * @example
 * // Using with automatic data fetching (requires league context)
 * function MyMonthView() {
 *   const {
 *     currentMonth,
 *     monthDays,
 *     availabilityByDate,
 *     navigateMonth,
 *     goToToday
 *   } = useMonthCalendarData()
 *
 *   return (
 *     <div>
 *       <button onClick={() => navigateMonth('prev')}>Previous</button>
 *       <h2>{format(currentMonth, 'MMMM yyyy')}</h2>
 *       <button onClick={() => navigateMonth('next')}>Next</button>
 *       {monthDays.map(day => <DayCell key={day.dateString} day={day} />)}
 *     </div>
 *   )
 * }
 *
 * @example
 * // Using with external data
 * function MyMonthView({ availability, proposals, matches }) {
 *   const externalData = {
 *     userAvailability: availability,
 *     matchProposals: proposals,
 *     allMatches: matches,
 *     loading: false,
 *     handleAvailabilitySave: () => {},
 *     handleAvailabilityDelete: () => {},
 *     handleMatchProposalConfirm: () => {},
 *     handleProposalAction: () => {}
 *   }
 *
 *   const { monthDays, availabilityByDate } = useMonthCalendarData(externalData)
 *
 *   return (
 *     // ... render calendar with external data
 *   )
 * }
 */
export function useMonthCalendarData(externalData) {
  // State for current month being displayed
  const [currentMonth, setCurrentMonth] = useState(() =>
    startOfMonth(new Date()),
  )

  // Only fetch data if external data is not provided
  // This avoids unnecessary API calls and hook violations
  const weeklyData = useWeeklyCalendarData({ skip: !!externalData })

  // Use external data if provided, otherwise fall back to weekly data
  const dataSource = externalData || weeklyData

  // Generate 42-day grid for current month
  const monthDays = useMemo(() => {
    return createMonthGrid(currentMonth)
  }, [currentMonth])

  // Expand recurring availabilities for the current month's date range
  const expandedUserAvailability = useMemo(() => {
    if (
      !dataSource.userAvailability ||
      dataSource.userAvailability.length === 0
    ) {
      return []
    }

    // Get the range for the current month view (includes leading/trailing days)
    const rangeStart = startOfDay(monthDays[0]?.date || currentMonth)
    const rangeEnd = endOfDay(
      monthDays[monthDays.length - 1]?.date || addMonths(currentMonth, 1),
    )

    // Expand all availabilities
    const expanded = []
    dataSource.userAvailability.forEach((availability) => {
      const slots = expandRecurrence(availability, rangeStart, rangeEnd)
      expanded.push(...slots)
    })

    return expanded
  }, [dataSource.userAvailability, monthDays, currentMonth])

  // Expand recurring availabilities for opponents
  const expandedOpponentAvailability = useMemo(() => {
    if (
      !dataSource.opponentAvailability ||
      dataSource.opponentAvailability.length === 0
    ) {
      return []
    }

    // Get the range for the current month view (includes leading/trailing days)
    const rangeStart = startOfDay(monthDays[0]?.date || currentMonth)
    const rangeEnd = endOfDay(
      monthDays[monthDays.length - 1]?.date || addMonths(currentMonth, 1),
    )

    // Expand all opponent availabilities
    const expanded = []
    dataSource.opponentAvailability.forEach((availability) => {
      const slots = expandRecurrence(availability, rangeStart, rangeEnd)
      expanded.push(...slots)
    })

    return expanded
  }, [dataSource.opponentAvailability, monthDays, currentMonth])

  // Group availability data by date (using expanded data)
  const availabilityByDate = useMemo(() => {
    return groupAvailabilityByDate(expandedUserAvailability)
  }, [expandedUserAvailability])

  // Group opponent availability data by date (using expanded data)
  const opponentAvailabilityByDate = useMemo(() => {
    return groupOpponentAvailabilityByDate(expandedOpponentAvailability)
  }, [expandedOpponentAvailability])

  // Group match proposals by date
  const proposalsByDate = useMemo(() => {
    return groupProposalsByDate(dataSource.matchProposals)
  }, [dataSource.matchProposals])

  // Group matches by date
  const matchesByDate = useMemo(() => {
    return groupMatchesByDate(dataSource.allMatches)
  }, [dataSource.allMatches])

  // Navigate to previous or next month
  const navigateMonth = useCallback((direction) => {
    if (direction !== 'next' && direction !== 'prev') {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `Invalid direction "${direction}". Expected "next" or "prev".`,
        )
      }
      return
    }
    const monthsToAdd = direction === 'next' ? 1 : -1
    setCurrentMonth((prevMonth) => {
      const newMonth = addMonths(prevMonth, monthsToAdd)
      return startOfMonth(newMonth)
    })
  }, [])

  // Go to today's month
  const goToToday = useCallback(() => {
    setCurrentMonth(startOfMonth(new Date()))
  }, [])

  return {
    // State
    currentMonth,
    monthDays,
    availabilityByDate,
    opponentAvailabilityByDate,
    unplayedOpponents: dataSource.unplayedOpponents || [],
    divisionInfo: dataSource.divisionInfo || null,
    proposalsByDate,
    matchesByDate,
    loading: dataSource.loading,

    // Navigation
    navigateMonth,
    goToToday,

    // Actions (passed through from data source)
    handleAvailabilitySave: dataSource.handleAvailabilitySave,
    handleAvailabilityDelete: dataSource.handleAvailabilityDelete,
    handleMatchProposalConfirm: dataSource.handleMatchProposalConfirm,
    handleProposalAction: dataSource.handleProposalAction,
  }
}
